import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
	DndContext,
	DragOverlay,
	MeasuringStrategy,
	MouseSensor,
	TouchSensor,
	closestCenter,
	pointerWithin,
	useSensor,
	useSensors,
	type CollisionDetection,
	type DragEndEvent,
	type DragStartEvent,
	type Modifier,
} from '@dnd-kit/core';
import { CheckIcon, MinusIcon, TagIcon } from '@heroicons/react/24/outline';
import { ConfirmDialog } from '../ui';
import CategoryStrip from './CategoryStrip';
import ServiceBulkBar from './ServiceBulkBar';
import ServiceListView from './ServiceListView';
import { filterServices } from './serviceFilter';
import { ApiError } from '../../api';
import useServiceSelection from '../../hooks/useServiceSelection';
import useServiceCategoriesStore from '../../stores/serviceCategoriesStore';
import type { Service } from '../../stores/servicesStore';
import type { CategoryFilter, ServiceCategory } from '../../types/serviceCategory';

/**
 * Shift-click and drag-to-assign are accelerators, not the only way through —
 * so they're taught at the moment they'd help and retired once used, rather
 * than sitting above the grid as permanent instructions.
 */
const HINT_KEYS = {
	range: 'bbp_hint_range_select',
	drag: 'bbp_hint_drag_assign',
} as const;

type Gesture = keyof typeof HINT_KEYS;

/** Stop showing a hint once the provider has used the gesture this many times. */
const HINT_RETIRE_AFTER = 2;

const readHintCount = (gesture: Gesture): number => {
	try {
		return Number(window.localStorage.getItem(HINT_KEYS[gesture])) || 0;
	} catch {
		// Private browsing / storage disabled — the hints just keep showing.
		return 0;
	}
};

/** How far outside the strip still counts as aiming at it. */
const STRIP_DROP_MARGIN = 16;

const getEventPoint = (event: Event): { x: number; y: number } | null => {
	if ('touches' in event) {
		const touch = (event as TouchEvent).touches[0];
		return touch ? { x: touch.clientX, y: touch.clientY } : null;
	}
	if ('clientX' in event) {
		const mouse = event as MouseEvent;
		return { x: mouse.clientX, y: mouse.clientY };
	}
	return null;
};

/**
 * Pins the drag token just below and right of the cursor rather than wherever
 * the card happened to be grabbed, so it never sits on top of the category
 * strip the provider is aiming at.
 */
const followCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
	if (!activatorEvent || !draggingNodeRect) return transform;

	const point = getEventPoint(activatorEvent);
	if (!point) return transform;

	const GAP = 14;
	return {
		...transform,
		x: transform.x + point.x - draggingNodeRect.left + GAP,
		y: transform.y + point.y - draggingNodeRect.top + GAP,
	};
};

export interface ProviderServicesManagerProps {
	locationId: number;
	services: Service[];
	isLoading: boolean;
	error: string | null;
	onAdd: () => void;
	onView: (service: Service) => void;
	onEdit: (service: Service) => void;
	onDelete: (service: Service) => void;
	onRetry: () => void;
	/**
	 * Refetch services. Needed after anything that rewrites them behind the
	 * list: a bulk assign, a rename (the name is denormalised onto each
	 * service), or a delete (its services come back uncategorized).
	 */
	onServicesChanged: () => Promise<void> | void;
}

interface DragSession {
	activeId: string;
	ids: Set<string>;
	/** The drag picked up the whole selection, so a drop should clear it. */
	fromSelection: boolean;
}

/**
 * The Provider Settings → Services tab: the category strip, the service grid,
 * and everything that connects them — selection, bulk assign, and drag onto a
 * category. Provider-only; the staff Services page renders `ServiceListView`
 * directly and is unaffected by any of this.
 */
const ProviderServicesManager = ({
	locationId,
	services,
	isLoading,
	error,
	onAdd,
	onView,
	onEdit,
	onDelete,
	onRetry,
	onServicesChanged,
}: ProviderServicesManagerProps) => {
	const { categories, fetchCategories, createCategory, renameCategory, deleteCategory, assignServices } =
		useServiceCategoriesStore();

	const [searchTerm, setSearchTerm] = useState('');
	const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
	const [deletingCategory, setDeletingCategory] = useState<ServiceCategory | null>(null);
	const [isDeletingCategory, setIsDeletingCategory] = useState(false);
	const [busyCategoryId, setBusyCategoryId] = useState<number | null>(null);
	const [isAssigning, setIsAssigning] = useState(false);
	const [drag, setDrag] = useState<DragSession | null>(null);
	const [hintCounts, setHintCounts] = useState(() => ({
		range: readHintCount('range'),
		drag: readHintCount('drag'),
	}));
	const stripRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		fetchCategories(locationId);
	}, [locationId, fetchCategories]);

	// The list is filtered here as well as inside ServiceListView — same
	// function, same arguments, so "select all matching" and shift-click ranges
	// operate on exactly the cards on screen.
	const visibleServices = useMemo(
		() => filterServices(services, searchTerm, categoryFilter),
		[services, searchTerm, categoryFilter]
	);
	const visibleIds = useMemo(() => visibleServices.map(s => s.id), [visibleServices]);
	const allIds = useMemo(() => services.map(s => s.id), [services]);

	const { selectedIds, toggle, selectVisible, deselectVisible, clear, hasUsedRange } =
		useServiceSelection(visibleIds, allIds);

	const markGestureUsed = useCallback((gesture: Gesture) => {
		setHintCounts(prev => {
			const next = prev[gesture] + 1;
			try {
				window.localStorage.setItem(HINT_KEYS[gesture], String(next));
			} catch {
				// Nothing to do — the hint stays visible, which is harmless.
			}
			return { ...prev, [gesture]: next };
		});
	}, []);

	// The selection hook reports the gesture; recording it is this component's job.
	useEffect(() => {
		if (hasUsedRange) markGestureUsed('range');
		// Fires once, when the first shift-click lands.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasUsedRange]);

	// A category can vanish under the filter — deleted here, or removed from
	// another tab and picked up by the next refetch.
	useEffect(() => {
		if (typeof categoryFilter === 'number' && !categories.some(c => c.id === categoryFilter)) {
			setCategoryFilter('all');
		}
	}, [categories, categoryFilter]);

	const uncategorizedCount = useMemo(
		() => services.filter(s => s.categoryId == null).length,
		[services]
	);

	const selectedCount = selectedIds.size;
	const visibleSelectedCount = useMemo(
		() => visibleIds.filter(id => selectedIds.has(id)).length,
		[visibleIds, selectedIds]
	);
	const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;

	const hint = useMemo(() => {
		if (selectedCount === 1 && hintCounts.range < HINT_RETIRE_AFTER) {
			return 'Shift-click another to select a range';
		}
		if (
			selectedCount > 1 &&
			categories.length > 0 &&
			hintCounts.drag < HINT_RETIRE_AFTER
		) {
			return 'Or drag them onto a category above';
		}
		return null;
	}, [selectedCount, categories.length, hintCounts]);

	const assign = useCallback(
		async (categoryId: number, ids: string[], clearAfter: boolean) => {
			if (ids.length === 0) return;

			const category = categories.find(c => c.id === categoryId);
			setIsAssigning(true);
			setBusyCategoryId(categoryId);
			try {
				await assignServices(locationId, categoryId, ids.map(Number));
				await onServicesChanged();
				toast.success(
					`${ids.length} service${ids.length !== 1 ? 's' : ''} moved to "${
						category?.name ?? 'category'
					}"`
				);
				if (clearAfter) clear();
			} catch (err) {
				const apiError = err as ApiError;
				toast.error(apiError.message || 'Failed to assign the category');
			} finally {
				setIsAssigning(false);
				setBusyCategoryId(null);
			}
		},
		[assignServices, categories, clear, locationId, onServicesChanged]
	);

	const handleCreateCategory = useCallback(
		async (name: string) => {
			const created = await createCategory(locationId, name);
			toast.success(`Category "${created.name}" created`);
		},
		[createCategory, locationId]
	);

	const handleRenameCategory = useCallback(
		async (categoryId: number, name: string) => {
			setBusyCategoryId(categoryId);
			try {
				await renameCategory(locationId, categoryId, name);
				// The name is stamped onto every service under it, so the cards
				// are stale until the list comes back.
				await onServicesChanged();
				toast.success(`Category renamed to "${name}"`);
			} finally {
				setBusyCategoryId(null);
			}
		},
		[locationId, onServicesChanged, renameCategory]
	);

	const confirmDeleteCategory = useCallback(async () => {
		if (!deletingCategory) return;

		setIsDeletingCategory(true);
		try {
			await deleteCategory(locationId, deletingCategory.id);
			await onServicesChanged();
			toast.success(`Category "${deletingCategory.name}" deleted`);
			setDeletingCategory(null);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete the category');
		} finally {
			setIsDeletingCategory(false);
		}
	}, [deleteCategory, deletingCategory, locationId, onServicesChanged]);

	// Mouse drags start after a short movement so clicks still land; touch needs
	// a press-and-hold instead, otherwise the gesture would eat page scrolling.
	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
		useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
	);

	/**
	 * Exact hit-testing first, then a forgiving fallback: anywhere over the
	 * strip snaps to the nearest chip. Without it the pointer has to land inside
	 * a small pill; with it, "somewhere around Coloring" is enough. The bounds
	 * check is what keeps a stray drop elsewhere on the page from assigning
	 * anything at all.
	 */
	const collisionDetection: CollisionDetection = useCallback(args => {
		const exact = pointerWithin(args);
		if (exact.length > 0) return exact;

		const pointer = args.pointerCoordinates;
		const strip = stripRef.current?.getBoundingClientRect();
		if (
			pointer &&
			strip &&
			pointer.x >= strip.left - STRIP_DROP_MARGIN &&
			pointer.x <= strip.right + STRIP_DROP_MARGIN &&
			pointer.y >= strip.top - STRIP_DROP_MARGIN &&
			pointer.y <= strip.bottom + STRIP_DROP_MARGIN
		) {
			return closestCenter(args);
		}

		return [];
	}, []);

	const handleDragStart = (event: DragStartEvent) => {
		const activeId = String(event.active.id);
		const fromSelection = selectedIds.has(activeId);
		setDrag({
			activeId,
			ids: new Set(fromSelection ? selectedIds : [activeId]),
			fromSelection,
		});
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const session = drag;
		setDrag(null);
		if (!session) return;

		const categoryId = event.over?.data.current?.categoryId as number | undefined;
		if (categoryId == null) return;

		markGestureUsed('drag');
		void assign(categoryId, [...session.ids], session.fromSelection);
	};

	const draggedService = drag ? services.find(s => s.id === drag.activeId) : undefined;
	const draggedCount = drag?.ids.size ?? 0;

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={collisionDetection}
			// The strip grows into a drop board once a drag starts, so the chip
			// rects measured at drag start would be stale. Re-measure instead.
			measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={() => setDrag(null)}
		>
			<CategoryStrip
				containerRef={stripRef}
				categories={categories}
				activeFilter={categoryFilter}
				onFilterChange={setCategoryFilter}
				allCount={services.length}
				uncategorizedCount={uncategorizedCount}
				onCreate={handleCreateCategory}
				onRename={handleRenameCategory}
				onDelete={setDeletingCategory}
				busyCategoryId={busyCategoryId}
				isDragActive={!!drag}
			/>

			<ServiceListView
				services={services}
				isLoading={isLoading}
				error={error}
				searchTerm={searchTerm}
				onSearchChange={setSearchTerm}
				canManage
				onAdd={onAdd}
				onView={onView}
				onEdit={onEdit}
				onDelete={onDelete}
				onRetry={onRetry}
				categoryFilter={categoryFilter}
				onToggleSelect={toggle}
				selectedIds={selectedIds}
				draggingIds={drag?.ids}
				toolbarSlot={
					visibleIds.length > 0 ? (
						<button
							type="button"
							className="service-toolbar__select-all"
							onClick={allVisibleSelected ? deselectVisible : selectVisible}
						>
							{allVisibleSelected ? (
								<MinusIcon className="w-4 h-4" />
							) : (
								<CheckIcon className="w-4 h-4" />
							)}
							{allVisibleSelected
								? `Deselect ${visibleIds.length}`
								: `Select all ${visibleIds.length}`}
						</button>
					) : null
				}
			/>

			{/* A small token rather than a replica card: a full-size card under
			    the cursor covers the very chips it is being dragged towards. */}
			<DragOverlay dropAnimation={null} modifiers={[followCursor]} className="drag-overlay">
				{draggedService && (
					<span className="drag-token">
						<TagIcon className="w-4 h-4 flex-shrink-0" />
						<span className="drag-token__label">
							{draggedCount > 1
								? `${draggedCount} services`
								: draggedService.name}
						</span>
					</span>
				)}
			</DragOverlay>

			<ServiceBulkBar
				count={selectedCount}
				hiddenCount={selectedCount - visibleSelectedCount}
				categories={categories}
				onAssign={categoryId => void assign(categoryId, [...selectedIds], true)}
				onClear={clear}
				isAssigning={isAssigning}
				hint={hint}
			/>

			<ConfirmDialog
				isOpen={!!deletingCategory}
				onClose={() => setDeletingCategory(null)}
				onConfirm={confirmDeleteCategory}
				title="Delete Category"
				message={
					deletingCategory
						? `Delete "${deletingCategory.name}"? ${
								deletingCategory.serviceCount === 0
									? 'No services are filed under it.'
									: `${deletingCategory.serviceCount} service${
											deletingCategory.serviceCount !== 1 ? 's' : ''
										} will be kept and become uncategorized.`
							}`
						: ''
				}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeletingCategory}
			/>
		</DndContext>
	);
};

export default ProviderServicesManager;
